import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { z } from 'zod';

const passwordSchema = z.object({
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type PasswordData = z.infer<typeof passwordSchema>;

const SetPassword = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState<PasswordData>({ password: '', confirmPassword: '' });
  const [errors, setErrors] = useState<Partial<Record<keyof PasswordData, string>>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [intent, setIntent] = useState<{ name: string; email: string } | null>(null);
  const [noIntent, setNoIntent] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem('freemiumIntent');
    const legacyEmail = sessionStorage.getItem('freemiumEmail');
    const legacyName = sessionStorage.getItem('freemiumName');

    if (stored && stored !== 'true') {
      try {
        const parsed = JSON.parse(stored);
        if (parsed?.email && parsed?.name) {
          setIntent({
            email: String(parsed.email).toLowerCase().trim(),
            name: String(parsed.name),
          });
          sessionStorage.removeItem('freemiumEmail');
          sessionStorage.removeItem('freemiumName');
          return;
        }
      } catch {
        // fallthrough
      }
    }

    if (legacyEmail && legacyName) {
      const newIntent = { email: legacyEmail.toLowerCase().trim(), name: legacyName };
      sessionStorage.setItem('freemiumIntent', JSON.stringify(newIntent));
      sessionStorage.removeItem('freemiumEmail');
      sessionStorage.removeItem('freemiumName');
      setIntent(newIntent);
      return;
    }

    setNoIntent(true);
  }, []);

  useEffect(() => {
    if (noIntent) {
      toast.error('Please start from the signup page.');
      navigate('/apply', { replace: true });
    }
  }, [noIntent, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!intent) return;

    const result = passwordSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof PasswordData, string>> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as keyof PasswordData] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    setIsLoading(true);

    const email = intent.email;
    const password = formData.password;

    const { error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: { name: intent.name },
      },
    });

    if (signUpError) {
      if (signUpError.message.includes('already registered')) {
        toast.info('Account already exists, signing you in…');
      } else {
        setIsLoading(false);
        toast.error(signUpError.message);
        return;
      }
    }

    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });

    if (signInError) {
      setIsLoading(false);
      if (signInError.message.includes('Invalid login credentials')) {
        setErrors({ password: 'Incorrect password. Try resetting it.' });
      } else {
        toast.error(signInError.message);
      }
      return;
    }

    try {
      await supabase.functions.invoke('link-client-to-user', { body: { email } });
    } catch (e) {
      console.warn('link-client-to-user failed:', e);
    }

    sessionStorage.removeItem('freemiumIntent');
    toast.success('Welcome! Redirecting to your dashboard…');
    navigate('/dashboard', { replace: true });
  };

  if (!intent) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-40 pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-100 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 py-8 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              Set Your Password
            </h1>
            <p className="text-slate-600 text-sm">
              Welcome {intent.name}! Just one more step to access the Members Area.
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-700 text-sm font-medium">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-slate-50 border-slate-200 focus:border-blue-500 text-slate-900 placeholder:text-slate-400 pr-12 h-12 rounded-xl"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-2 -m-2"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs">{errors.password}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-slate-700 text-sm font-medium">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirm ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="bg-slate-50 border-slate-200 focus:border-blue-500 text-slate-900 placeholder:text-slate-400 pr-12 h-12 rounded-xl"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-2 -m-2"
                  >
                    {showConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-xs">{errors.confirmPassword}</p>}
              </div>

              <Button
                type="submit"
                size="lg"
                className="w-full h-14 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl shadow-lg shadow-blue-600/25 active:scale-[0.98]"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Creating…
                  </>
                ) : (
                  'Enter Members Area'
                )}
              </Button>
            </form>
          </div>

          {/* Trust indicator */}
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500">
            <Shield className="w-4 h-4 text-green-500" />
            <span>Your information is secure</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SetPassword;
